import type {
  Confidence,
  FindingAggregateResponse,
  FindingAggregateResponseStatistics,
  FindingResponse,
  FindingResultResponse,
  Severity,
  SeverityUpdateFilter,
  TriageSuggestedStatusResponse,
} from '../src/utils/api-client/api-schemas'
import { SCAN_FINDINGS_PARAMS } from '../src/utils/api-client/query-params'

// ---------------------------------------------------------------------------
// MockFinding — simplified intermediate representation
//
// Fields are derived from real API schema types where possible.
// Some use mock-specific unions where the mock representation diverges
// from the API (e.g. status enums that combine outcome + status concepts).
// ---------------------------------------------------------------------------

export type MockFinding = {
  id: string
  title: string
  rule: string
  /**
   * `rank` is on the same 0-10 scale as the score query parameters, not the platform's
   * `round(score * 10)`. See `isRankWithinScoreRange` before changing these values.
   */
  severity: { label: string; rank: number }
  /**
   * Contextual CVSS for the findings-grid `CVSS` column. `baseCvss` is the advisory score;
   * `effectiveCvss` is the contextual (Pixee-adjusted) score the column shows — equal to base
   * when unchanged, lower when the environment reduced it. Omit both for findings with no CVSS
   * (the column renders `-`). Same `rank` convention as `severity` above.
   */
  effectiveCvss?: Severity
  baseCvss?: Severity
  /** Same `rank` convention as `severity` above — read that note before changing these values. */
  suggestedSeverity: { label: string; rank: number } | null
  triageOutcome: TriageSuggestedStatusResponse | 'likely_true_positive' | null
  triageStatus: 'completed' | 'failed' | 'no_recommendations' | 'in_progress' | null
  fixStatus: 'completed' | 'failed' | 'no_recommendations' | 'in_progress' | null
  fixConfidence: Confidence | null
  fixOutcome: 'has_fix' | 'blocked' | 'excluded' | null
  scaStatus: 'completed' | 'failed' | 'blocked' | 'excluded' | 'in_progress' | null
  scaClassification: 'exploitable' | 'not_exploitable' | 'inconclusive' | null
  isReadyToFix: boolean
  isNoFixNecessary: boolean
  analyzed: boolean
  severityUpdate: SeverityUpdateFilter | null
  patchStatus: 'issued' | 'merged' | null
}

// ---------------------------------------------------------------------------
// Static finding lists
// ---------------------------------------------------------------------------

export const SAST_FINDINGS: readonly MockFinding[] = [
  // ── true_positive (8) ──────────────────────────────────────────────────
  {
    id: 'sast-1',
    title: 'SQL Injection in UserController',
    rule: 'java:S3649',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'merged',
  },
  {
    id: 'sast-2',
    title: 'Hardcoded credentials in config',
    rule: 'java:S2068',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'increased',
    patchStatus: 'issued',
  },
  {
    id: 'sast-3',
    title: 'XSS in template rendering',
    rule: 'java:S5131',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'medium',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'issued',
  },
  {
    id: 'sast-4',
    title: 'Command injection in ProcessBuilder',
    rule: 'java:S2076',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'merged',
  },
  {
    id: 'sast-5',
    title: 'Insecure cookie without HttpOnly flag',
    rule: 'java:S3330',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'medium',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-6',
    title: 'XML external entity injection',
    rule: 'java:S2755',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'low',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'increased',
    patchStatus: null,
  },
  {
    id: 'sast-7',
    title: 'Unencrypted socket connection',
    rule: 'java:S5332',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'blocked',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'increased',
    patchStatus: null,
  },
  {
    id: 'sast-8',
    title: 'Insecure random number generator',
    rule: 'java:S2245',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'issued',
  },
  // ── likely_true_positive (6) ───────────────────────────────────────────
  {
    id: 'sast-9',
    title: 'Insecure deserialization detected',
    rule: 'java:S5135',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'medium',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'decreased',
    patchStatus: null,
  },
  {
    id: 'sast-10',
    title: 'LDAP injection vulnerability',
    rule: 'java:S2078',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'blocked',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-11',
    title: 'Expression language injection',
    rule: 'java:S5334',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'high', rank: 8.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'medium',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'issued',
  },
  {
    id: 'sast-12',
    title: 'Regex denial of service',
    rule: 'java:S2631',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-13',
    title: 'Unsafe reflection usage',
    rule: 'java:S3011',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'decreased',
    patchStatus: null,
  },
  {
    id: 'sast-14',
    title: 'Server-side template injection',
    rule: 'java:S6396',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: { label: 'critical', rank: 9.0 },
    triageOutcome: 'likely_true_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'high',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: true,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: 'merged',
  },
  // ── inconclusive (4) ──────────────────────────────────────────────────
  {
    id: 'sast-15',
    title: 'Potential SSRF in URL fetch',
    rule: 'java:S5144',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triageOutcome: 'inconclusive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'decreased',
    patchStatus: null,
  },
  {
    id: 'sast-16',
    title: 'Possible information leak via error message',
    rule: 'java:S5164',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'inconclusive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'medium',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-17',
    title: 'Unchecked type cast',
    rule: 'java:S1948',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'inconclusive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'blocked',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-18',
    title: 'Ambiguous cryptographic usage',
    rule: 'java:S5547',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'inconclusive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: 'low',
    fixOutcome: 'has_fix',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: 'decreased',
    patchStatus: null,
  },
  // ── false_positive (5) ─────────────────────────────────────────────────
  {
    id: 'sast-19',
    title: 'Unused private method detected',
    rule: 'java:S1144',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'false_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-20',
    title: 'Potential null pointer dereference',
    rule: 'java:S2259',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'false_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-21',
    title: 'Redundant null check after instanceof',
    rule: 'java:S1872',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'false_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-22',
    title: 'Dead code in exception handler',
    rule: 'java:S1763',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'false_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-23',
    title: 'Flagged logging of sensitive data',
    rule: 'java:S5145',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'low', rank: 4.0 },
    triageOutcome: 'false_positive',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'decreased',
    patchStatus: null,
  },
  // ── wont_fix (3) ───────────────────────────────────────────────────────
  {
    id: 'sast-24',
    title: 'Weak cryptographic algorithm',
    rule: 'java:S5542',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: { label: 'medium', rank: 6.0 },
    triageOutcome: 'wont_fix',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-25',
    title: 'Deprecated API usage in legacy module',
    rule: 'java:S1874',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'wont_fix',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  {
    id: 'sast-26',
    title: 'Hardcoded IP address in config',
    rule: 'java:S1313',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: { label: 'low', rank: 3.0 },
    triageOutcome: 'wont_fix',
    triageStatus: 'completed',
    fixStatus: 'completed',
    fixConfidence: null,
    fixOutcome: 'excluded',
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: 'no_update',
    patchStatus: null,
  },
  // ── not_triaged (2) ────────────────────────────────────────────────────
  {
    id: 'sast-27',
    title: 'Information exposure through error',
    rule: 'java:S5164',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: null,
    triageOutcome: 'not_triaged',
    triageStatus: 'completed',
    fixStatus: 'no_recommendations',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-28',
    title: 'Missing input validation',
    rule: 'java:S5145',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triageOutcome: 'not_triaged',
    triageStatus: 'completed',
    fixStatus: 'no_recommendations',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // ── triage in_progress (3) ─────────────────────────────────────────────
  {
    id: 'sast-29',
    title: 'Potential HTTP response splitting',
    rule: 'java:S5167',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: 'in_progress',
    fixStatus: 'in_progress',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-30',
    title: 'Cleartext storage of sensitive data',
    rule: 'java:S5443',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: 'in_progress',
    fixStatus: 'in_progress',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-31',
    title: 'Insufficient session expiration',
    rule: 'java:S3415',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: 'in_progress',
    fixStatus: 'in_progress',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  // ── triage error (2) ───────────────────────────────────────────────────
  {
    id: 'sast-32',
    title: 'Unsafe YAML parsing',
    rule: 'java:S5361',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triageOutcome: 'error',
    triageStatus: 'failed',
    fixStatus: 'failed',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-33',
    title: 'Improper certificate validation',
    rule: 'java:S4830',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triageOutcome: 'error',
    triageStatus: 'failed',
    fixStatus: 'failed',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // ── triage excluded (2) ──────────────────────────────────────────────
  {
    id: 'sast-34',
    title: 'Overly permissive file permissions',
    rule: 'java:S2612',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triageOutcome: 'excluded',
    triageStatus: 'no_recommendations',
    fixStatus: 'no_recommendations',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-35',
    title: 'Unvalidated dynamic method invocation',
    rule: 'java:S3878',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triageOutcome: 'excluded',
    triageStatus: 'no_recommendations',
    fixStatus: 'no_recommendations',
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // ── null triageOutcome — unanalyzed (5) ────────────────────────────────
  {
    id: 'sast-36',
    title: 'Path traversal in file upload',
    rule: 'java:S2083',
    severity: { label: 'critical', rank: 9.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-37',
    title: 'Race condition in file access',
    rule: 'java:S3064',
    severity: { label: 'low', rank: 3.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-38',
    title: 'Unvalidated redirect target',
    rule: 'java:S5147',
    severity: { label: 'medium', rank: 6.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-39',
    title: 'Weak password hashing algorithm',
    rule: 'java:S5344',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sast-40',
    title: 'Missing CSRF token validation',
    rule: 'java:S4502',
    severity: { label: 'high', rank: 8.0 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
]

export const SCA_FINDINGS: readonly MockFinding[] = [
  // exploitable (2)
  {
    id: 'sca-1',
    title: 'CVE-2023-1234 in log4j',
    rule: 'sca:CVE-2023-1234',
    severity: { label: 'critical', rank: 9.8 },
    // Environment-reduced: advisory 9.8, contextual 3.1 (accented + tooltip).
    baseCvss: { label: '9.8', rank: 9.8 },
    effectiveCvss: { label: '3.1', rank: 3.1 },
    suggestedSeverity: { label: 'critical', rank: 5 },
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: 'completed',
    scaClassification: 'exploitable',
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sca-2',
    title: 'CVE-2023-5678 in commons-text',
    rule: 'sca:CVE-2023-5678',
    severity: { label: 'high', rank: 7.5 },
    // Unchanged: contextual equals base (normal color, no tooltip).
    baseCvss: { label: '7.5', rank: 7.5 },
    effectiveCvss: { label: '7.5', rank: 7.5 },
    suggestedSeverity: { label: 'high', rank: 4 },
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: 'completed',
    scaClassification: 'exploitable',
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // not_exploitable (2)
  {
    id: 'sca-3',
    title: 'CVE-2023-9012 in guava',
    rule: 'sca:CVE-2023-9012',
    severity: { label: 'medium', rank: 5.3 },
    // Not exploitable in this codebase: contextual reduced to 0 (accented + tooltip).
    baseCvss: { label: '5.3', rank: 5.3 },
    effectiveCvss: { label: '0.0', rank: 0 },
    suggestedSeverity: { label: 'medium', rank: 3 },
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: 'completed',
    scaClassification: 'not_exploitable',
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  {
    id: 'sca-4',
    title: 'CVE-2023-3456 in jackson-databind',
    rule: 'sca:CVE-2023-3456',
    severity: { label: 'low', rank: 2.1 },
    suggestedSeverity: { label: 'low', rank: 2 },
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: 'completed',
    scaClassification: 'not_exploitable',
    isReadyToFix: false,
    isNoFixNecessary: true,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // inconclusive (1)
  {
    id: 'sca-5',
    title: 'CVE-2023-7890 in spring-core',
    rule: 'sca:CVE-2023-7890',
    severity: { label: 'medium', rank: 4.7 },
    suggestedSeverity: { label: 'high', rank: 4 },
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: 'failed',
    scaClassification: 'inconclusive',
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: true,
    severityUpdate: null,
    patchStatus: null,
  },
  // null — unanalyzed (1)
  {
    id: 'sca-6',
    title: 'CVE-2023-2345 in netty',
    rule: 'sca:CVE-2023-2345',
    severity: { label: 'high', rank: 8.1 },
    suggestedSeverity: null,
    triageOutcome: null,
    triageStatus: null,
    fixStatus: null,
    fixConfidence: null,
    fixOutcome: null,
    scaStatus: null,
    scaClassification: null,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: false,
    severityUpdate: null,
    patchStatus: null,
  },
]

// ---------------------------------------------------------------------------
// Param → field value mapping constants
// ---------------------------------------------------------------------------

const TRIAGE_PARAM_TO_OUTCOME: Record<string, string> = {
  suspicious: 'likely_true_positive',
  true_positive: 'true_positive',
  inconclusive: 'inconclusive',
  false_positive: 'false_positive',
  wont_fix: 'wont_fix',
  blocked: 'blocked',
  excluded: 'excluded',
  not_triaged: 'not_triaged',
}

const OUTCOME_TO_TRIAGE_PARAM: Record<string, string> = {
  likely_true_positive: 'suspicious',
  true_positive: 'true_positive',
  inconclusive: 'inconclusive',
  false_positive: 'false_positive',
  wont_fix: 'wont_fix',
  blocked: 'blocked',
  excluded: 'excluded',
  not_triaged: 'not_triaged',
}

const SCA_PARAM_TO_FIELD: Record<string, string> = {
  'not-exploitable': 'not_exploitable',
  exploitable: 'exploitable',
  inconclusive: 'inconclusive',
}

const SCA_FIELD_TO_PARAM: Record<string, string> = {
  not_exploitable: 'not-exploitable',
  exploitable: 'exploitable',
  inconclusive: 'inconclusive',
}

const FIX_CONFIDENCE_PARAM_TO_FIELD: Record<string, string> = {
  'no-rating': 'no-rating',
  high: 'high',
  medium: 'medium',
  low: 'low',
}

// ---------------------------------------------------------------------------
// Filter predicates
// ---------------------------------------------------------------------------

export type FindingPredicate = (finding: MockFinding) => boolean

export const byTriageSuggestedStatus = (statuses: string[]): FindingPredicate => {
  const mapped = statuses.map(s => TRIAGE_PARAM_TO_OUTCOME[s] ?? s)
  return f => f.triageOutcome !== null && mapped.includes(f.triageOutcome)
}

export const byTriageStatus =
  (statuses: string[]): FindingPredicate =>
  f =>
    f.triageStatus !== null && statuses.includes(f.triageStatus)

const FIX_STATUS_PARAM_TO_FIELD: Record<string, string> = {
  'no-recommendations-available': 'no_recommendations',
  'out-of-scope': 'out-of-scope',
}

export const byFixStatus =
  (statuses: string[]): FindingPredicate =>
  f => {
    // The API's fix-status param covers both processing status and outcome:
    // - completed, failed, in_progress, no-recommendations-available → match fixStatus
    // - blocked, excluded → match fixOutcome
    const mapped = statuses.map(s => FIX_STATUS_PARAM_TO_FIELD[s] ?? s)
    const matchesStatus = f.fixStatus !== null && mapped.includes(f.fixStatus)
    const matchesOutcome = f.fixOutcome !== null && mapped.includes(f.fixOutcome)
    return matchesStatus || matchesOutcome
  }

export const byFixConfidence = (confidences: string[]): FindingPredicate => {
  const mapped = confidences.map(c => FIX_CONFIDENCE_PARAM_TO_FIELD[c] ?? c)
  return f => f.fixConfidence !== null && mapped.includes(f.fixConfidence)
}

export const byScaClassification = (classifications: string[]): FindingPredicate => {
  const mapped = classifications.map(c => SCA_PARAM_TO_FIELD[c] ?? c)
  return f => f.scaClassification !== null && mapped.includes(f.scaClassification)
}

export const byScaStatus =
  (statuses: string[]): FindingPredicate =>
  f =>
    f.scaStatus !== null && statuses.includes(f.scaStatus)

export const byAnalyzed =
  (analyzed: boolean): FindingPredicate =>
  f =>
    f.analyzed === analyzed

export const bySeverityLabel =
  (labels: string[]): FindingPredicate =>
  f => {
    const lower = labels.map(l => l.toLowerCase())
    return lower.includes(f.severity.label.toLowerCase())
  }

export const bySuggestedSeverityLabel =
  (labels: string[]): FindingPredicate =>
  f => {
    const lower = labels.map(l => l.toLowerCase())
    return f.suggestedSeverity !== null && lower.includes(f.suggestedSeverity.label.toLowerCase())
  }

/**
 * Compares a mock severity rank against the inclusive bounds of a score-range query parameter.
 *
 * Mock ranks share the 0-10 scale of the `minimum/maximum-severity-score` parameters, so the bounds
 * are compared against them directly. The platform does not: it persists rank as
 * `round(score * 10)` and converts the bounds server-side, so a real rank of `89` answers a bound of
 * `8.9`. Both range predicates go through here so that difference is stated once rather than
 * duplicated, and `mock-findings.test.ts` pins the mock convention — if the fixtures above are ever
 * restated in platform-shaped ranks, that test fails instead of every score filter silently
 * matching nothing.
 */
const isRankWithinScoreRange = (rank: number, min: number | null, max: number | null): boolean => {
  if (min !== null && rank < min) return false
  if (max !== null && rank > max) return false
  return true
}

export const bySeverityScoreRange =
  (min: number | null, max: number | null): FindingPredicate =>
  f =>
    isRankWithinScoreRange(f.severity.rank, min, max)

export const bySuggestedSeverityScoreRange =
  (min: number | null, max: number | null): FindingPredicate =>
  f =>
    // A finding with no suggested severity cannot satisfy a range, mirroring the platform's
    // outcome-type guard on the same filter.
    f.suggestedSeverity !== null && isRankWithinScoreRange(f.suggestedSeverity.rank, min, max)

export const bySeverityUpdate =
  (updates: string[]): FindingPredicate =>
  f =>
    f.severityUpdate !== null && updates.includes(f.severityUpdate)

export const byPatchStatus =
  (statuses: string[]): FindingPredicate =>
  f =>
    f.patchStatus !== null && statuses.includes(f.patchStatus)

// ---------------------------------------------------------------------------
// Compose
// ---------------------------------------------------------------------------

export const composePredicates = (predicates: FindingPredicate[]): FindingPredicate =>
  predicates.length === 0 ? () => true : finding => predicates.every(p => p(finding))

export const buildFilterFromParams = (params: URLSearchParams): FindingPredicate => {
  const predicates: FindingPredicate[] = []

  const triageSuggested = params.getAll(SCAN_FINDINGS_PARAMS.triageSuggestedStatus)
  if (triageSuggested.length > 0) predicates.push(byTriageSuggestedStatus(triageSuggested))

  const triageStatus = params.getAll(SCAN_FINDINGS_PARAMS.triageStatus)
  if (triageStatus.length > 0) predicates.push(byTriageStatus(triageStatus))

  const fixStatus = params.getAll(SCAN_FINDINGS_PARAMS.fixStatus)
  if (fixStatus.length > 0) predicates.push(byFixStatus(fixStatus))

  const fixConfidences = params.getAll(SCAN_FINDINGS_PARAMS.fixConfidences)
  if (fixConfidences.length > 0) predicates.push(byFixConfidence(fixConfidences))

  const scaClassifications = params.getAll(SCAN_FINDINGS_PARAMS.scaClassifications)
  if (scaClassifications.length > 0) predicates.push(byScaClassification(scaClassifications))

  const scaStatusParams = params.getAll(SCAN_FINDINGS_PARAMS.scaStatus)
  if (scaStatusParams.length > 0) predicates.push(byScaStatus(scaStatusParams))

  const analyzedParam = params.get(SCAN_FINDINGS_PARAMS.analyzed)
  if (analyzedParam !== null) predicates.push(byAnalyzed(analyzedParam === 'true'))

  const severityLabels = params.getAll(SCAN_FINDINGS_PARAMS.includeSeverityLabel)
  if (severityLabels.length > 0) predicates.push(bySeverityLabel(severityLabels))

  const suggestedSeverityLabels = params.getAll(SCAN_FINDINGS_PARAMS.includeSuggestedSeverityLabel)
  if (suggestedSeverityLabels.length > 0) predicates.push(bySuggestedSeverityLabel(suggestedSeverityLabels))

  const minScore = params.get(SCAN_FINDINGS_PARAMS.minimumSeverityScore)
  const maxScore = params.get(SCAN_FINDINGS_PARAMS.maximumSeverityScore)
  if (minScore !== null || maxScore !== null) {
    predicates.push(bySeverityScoreRange(minScore ? Number(minScore) : null, maxScore ? Number(maxScore) : null))
  }

  const minSuggestedScore = params.get(SCAN_FINDINGS_PARAMS.minimumSuggestedSeverityScore)
  const maxSuggestedScore = params.get(SCAN_FINDINGS_PARAMS.maximumSuggestedSeverityScore)
  if (minSuggestedScore !== null || maxSuggestedScore !== null) {
    predicates.push(
      bySuggestedSeverityScoreRange(
        minSuggestedScore ? Number(minSuggestedScore) : null,
        maxSuggestedScore ? Number(maxSuggestedScore) : null
      )
    )
  }

  const severityUpdates = params.getAll(SCAN_FINDINGS_PARAMS.severityUpdates)
  if (severityUpdates.length > 0) predicates.push(bySeverityUpdate(severityUpdates))

  const patchStatuses = params.getAll(SCAN_FINDINGS_PARAMS.patchStatus)
  if (patchStatuses.length > 0) predicates.push(byPatchStatus(patchStatuses))

  return composePredicates(predicates)
}

// ---------------------------------------------------------------------------
// Aggregate stats reducer
// ---------------------------------------------------------------------------

export type AggregateStats = FindingAggregateResponseStatistics

export const emptyStats = (): AggregateStats => ({
  total: 0,
  completed_analysis: 0,
  in_progress_analysis: 0,
  triage: {
    status: { completed: 0, failed: 0, no_recommendations: 0, in_progress: 0 },
    outcome: {
      true_positive: 0,
      likely_true_positive: 0,
      inconclusive: 0,
      false_positive: 0,
      wont_fix: 0,
      blocked: 0,
      excluded: 0,
      not_triaged: 0,
    },
  },
  fix: {
    status: { completed: 0, failed: 0, no_recommendations: 0, in_progress: 0 },
    outcome: { has_fix: 0, blocked: 0, excluded: 0 },
    confidence: { high: 0, medium: 0, low: 0, no_rating: 0 },
  },
  sca: {
    status: { completed: 0, failed: 0, blocked: 0, excluded: 0, in_progress: 0 },
    classification: { exploitable: 0, not_exploitable: 0, inconclusive: 0 },
  },
  composite: { ready_to_fix: 0, no_fix_necessary: 0 },
})

export const accumulateFinding = (stats: AggregateStats, finding: MockFinding): AggregateStats => {
  stats.total++

  if (finding.analyzed) stats.completed_analysis++

  if (finding.triageOutcome) {
    stats.triage.outcome[finding.triageOutcome]++
  }
  if (finding.triageStatus) {
    stats.triage.status[finding.triageStatus]++
  }

  if (finding.fixConfidence && finding.fixOutcome === 'has_fix') {
    const key = finding.fixConfidence === 'no-rating' ? 'no_rating' : finding.fixConfidence
    stats.fix.confidence[key]++
  }
  if (finding.fixStatus) {
    stats.fix.status[finding.fixStatus]++
  }
  if (finding.fixOutcome) {
    stats.fix.outcome[finding.fixOutcome]++
  }

  if (finding.scaClassification) {
    stats.sca.classification[finding.scaClassification]++
  }
  if (finding.scaStatus) {
    stats.sca.status[finding.scaStatus]++
  }

  if (finding.isReadyToFix) stats.composite.ready_to_fix++
  if (finding.isNoFixNecessary) stats.composite.no_fix_necessary++

  return stats
}

export const filterAndAccumulate = (
  findings: readonly MockFinding[],
  predicate: FindingPredicate
): { items: MockFinding[]; stats: AggregateStats } =>
  findings.reduce<{ items: MockFinding[]; stats: AggregateStats }>(
    (acc, finding) => {
      if (predicate(finding)) {
        acc.items.push(finding)
        accumulateFinding(acc.stats, finding)
      }
      return acc
    },
    { items: [], stats: emptyStats() }
  )

// ---------------------------------------------------------------------------
// fromFindingResponse — converts FindingResponse → MockFinding
// ---------------------------------------------------------------------------

const TRIAGE_OUTCOME_TYPE_TO_STATUS: Record<string, MockFinding['triageStatus']> = {
  'completed-triage': 'completed',
  'failed-triage': 'failed',
  'no-recommendations-triage': 'no_recommendations',
}

const FIX_OUTCOME_TYPE_TO_STATUS: Record<string, MockFinding['fixStatus']> = {
  'completed-fix': 'completed',
  'failed-fix': 'failed',
  'no-recommendations-fix': 'no_recommendations',
}

const FIX_OUTCOME_TYPE_TO_FIX_OUTCOME: Record<string, MockFinding['fixOutcome']> = {
  'completed-fix': 'has_fix',
  'blocked-fix': 'blocked',
  'excluded-fix': 'excluded',
  'out-of-scope-fix': 'excluded',
}

const SCA_OUTCOME_TYPE_TO_STATUS: Record<string, MockFinding['scaStatus']> = {
  'completed-sca': 'completed',
  'failed-sca': 'failed',
  'blocked-sca': 'blocked',
  'excluded-sca': 'excluded',
}

const SCA_CLASSIFICATION_PARAM_TO_FIELD: Record<string, MockFinding['scaClassification']> = {
  exploitable: 'exploitable',
  'not-exploitable': 'not_exploitable',
  inconclusive: 'inconclusive',
}

export const fromFindingResponse = (finding: FindingResponse): MockFinding => {
  const results = finding._embedded?.['representative-results']?._embedded?.items ?? []
  const triageResult = results.find(r => r.type === 'triage')
  const fixResult = results.find(r => r.type === 'fix')
  const scaResult = results.find(r => r.type === 'sca')

  const triageOutcomeType = triageResult?.outcome?.type ?? null
  const fixOutcomeType = fixResult?.outcome?.type ?? null
  const scaOutcomeType = scaResult?.outcome?.type ?? null

  const triageOutcome: MockFinding['triageOutcome'] =
    triageOutcomeType === 'completed-triage' && triageResult?.outcome && 'suggested_status' in triageResult.outcome
      ? ((triageResult.outcome.suggested_status === 'suspicious'
          ? 'likely_true_positive'
          : triageResult.outcome.suggested_status) as MockFinding['triageOutcome'])
      : null

  const suggestedSeverity: MockFinding['suggestedSeverity'] =
    triageOutcomeType === 'completed-triage' && triageResult?.outcome && 'suggested_severity' in triageResult.outcome
      ? (triageResult.outcome.suggested_severity as MockFinding['suggestedSeverity'])
      : null

  const fixConfidence: MockFinding['fixConfidence'] =
    fixOutcomeType === 'completed-fix' && fixResult?.outcome && 'confidence' in fixResult.outcome
      ? (fixResult.outcome.confidence ?? null)
      : null

  const scaClassification: MockFinding['scaClassification'] =
    scaOutcomeType === 'completed-sca' && scaResult?.outcome && 'classification' in scaResult.outcome
      ? (SCA_CLASSIFICATION_PARAM_TO_FIELD[scaResult.outcome.classification] ?? null)
      : null

  const hasCompletedResult = results.some(r => Boolean(r.completed))

  return {
    id: finding.id,
    title: finding.title ?? '',
    rule: finding.rule,
    severity: {
      label: finding.severity?.label ?? 'unknown',
      rank: finding.severity?.rank ?? 0,
    },
    suggestedSeverity,
    triageOutcome,
    triageStatus: triageOutcomeType ? (TRIAGE_OUTCOME_TYPE_TO_STATUS[triageOutcomeType] ?? 'completed') : null,
    fixStatus: fixOutcomeType ? (FIX_OUTCOME_TYPE_TO_STATUS[fixOutcomeType] ?? 'completed') : null,
    fixConfidence,
    fixOutcome: fixOutcomeType ? (FIX_OUTCOME_TYPE_TO_FIX_OUTCOME[fixOutcomeType] ?? null) : null,
    scaStatus: scaOutcomeType ? (SCA_OUTCOME_TYPE_TO_STATUS[scaOutcomeType] ?? null) : null,
    scaClassification,
    isReadyToFix: false,
    isNoFixNecessary: false,
    analyzed: hasCompletedResult,
    severityUpdate: null,
    patchStatus: null,
    effectiveCvss: finding.effective_cvss,
    baseCvss: finding.base_cvss,
  }
}

// ---------------------------------------------------------------------------
// toFindingResponse — converts MockFinding to FindingResponse
// ---------------------------------------------------------------------------

const buildTriageResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  if (!finding.triageOutcome && !finding.triageStatus) return null

  const suggestedStatus = finding.triageOutcome
    ? (OUTCOME_TO_TRIAGE_PARAM[finding.triageOutcome] ?? finding.triageOutcome)
    : null

  if (finding.triageStatus === 'failed') {
    return {
      id: `triage-${findingId}`,
      type: 'triage' as const,
      completed: '2024-01-15T10:00:00Z',
      outcome: { type: 'failed-triage' as const, problem: { code: 'TRIAGE_FAILED', message: 'Triage failed' } },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  if (finding.triageStatus === 'no_recommendations') {
    return {
      id: `triage-${findingId}`,
      type: 'triage' as const,
      completed: '2024-01-15T10:00:00Z',
      outcome: { type: 'no-recommendations-triage' as const },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  // A triage still running has no outcome yet — that absence is exactly what
  // `getAnalysisIndicatorStatus` reads as "analysis in progress". Emitting a
  // completed-triage here would force a null `suggested_status` and fail the schema.
  if (finding.triageStatus === 'in_progress') {
    return {
      id: `triage-${findingId}`,
      type: 'triage' as const,
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  return {
    id: `triage-${findingId}`,
    type: 'triage' as const,
    completed: '2024-01-15T10:00:00Z',
    outcome: {
      type: 'completed-triage' as const,
      suggested_status: suggestedStatus as 'true_positive',
      suggested_severity: finding.suggestedSeverity ?? { label: 'unknown', rank: 0 },
      summary: `Triage result for ${finding.title}`,
    },
    _links: {
      self: { href: `#` },
      finding: { href: `#` },
      analysis: { href: '#' },
    },
  }
}

const buildFixResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  if (!finding.fixStatus && !finding.fixConfidence) return null

  if (finding.fixStatus === 'failed') {
    return {
      id: `fix-${findingId}`,
      type: 'fix' as const,
      completed: '2024-01-15T11:00:00Z',
      outcome: { type: 'failed-fix' as const, problem: { code: 'FIX_FAILED', message: 'Fix failed' } },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
        changesets: { href: '#' },
        patches: { href: '#' },
        'latest-patch': { href: '#' },
      },
    }
  }

  if (finding.fixStatus === 'no_recommendations') {
    return {
      id: `fix-${findingId}`,
      type: 'fix' as const,
      completed: '2024-01-15T11:00:00Z',
      outcome: { type: 'no-recommendations-fix' as const },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
        changesets: { href: '#' },
        patches: { href: '#' },
        'latest-patch': { href: '#' },
      },
    }
  }

  return {
    id: `fix-${findingId}`,
    type: 'fix' as const,
    completed: '2024-01-15T11:00:00Z',
    outcome: {
      type: 'completed-fix' as const,
      strategy: 'ai' as const,
      confidence: finding.fixConfidence ?? undefined,
    },
    _links: {
      self: { href: `#` },
      finding: { href: `#` },
      analysis: { href: '#' },
      changesets: { href: '#' },
      patches: { href: '#' },
      'latest-patch': { href: '#' },
    },
  }
}

const buildScaResult = (finding: MockFinding, findingId: string): FindingResultResponse | null => {
  if (!finding.scaStatus && !finding.scaClassification) return null

  if (finding.scaStatus === 'failed') {
    return {
      id: `sca-${findingId}`,
      type: 'sca' as const,
      completed: '2024-01-15T12:00:00Z',
      outcome: {
        type: 'failed-sca' as const,
        problem: { title: 'SCA Failed', detail: 'SCA analysis failed' },
      },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  if (finding.scaStatus === 'blocked') {
    return {
      id: `sca-${findingId}`,
      type: 'sca' as const,
      completed: '2024-01-15T12:00:00Z',
      outcome: { type: 'blocked-sca' as const },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  if (finding.scaStatus === 'excluded') {
    return {
      id: `sca-${findingId}`,
      type: 'sca' as const,
      completed: '2024-01-15T12:00:00Z',
      outcome: { type: 'excluded-sca' as const },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  if (finding.scaClassification) {
    const classification = SCA_FIELD_TO_PARAM[finding.scaClassification] ?? finding.scaClassification
    return {
      id: `sca-${findingId}`,
      type: 'sca' as const,
      completed: '2024-01-15T12:00:00Z',
      outcome: {
        type: 'completed-sca' as const,
        classification: classification as 'exploitable',
      },
      _links: {
        self: { href: `#` },
        finding: { href: `#` },
        analysis: { href: '#' },
      },
    }
  }

  return null
}

export const toFindingResponse = (finding: MockFinding): FindingResponse => {
  const results: FindingResultResponse[] = []

  const triageResult = buildTriageResult(finding, finding.id)
  if (triageResult) results.push(triageResult)

  const fixResult = buildFixResult(finding, finding.id)
  if (fixResult) results.push(fixResult)

  const scaResult = buildScaResult(finding, finding.id)
  if (scaResult) results.push(scaResult)

  return {
    id: finding.id,
    title: finding.title,
    rule: finding.rule,
    severity: finding.severity,
    effective_cvss: finding.effectiveCvss,
    base_cvss: finding.baseCvss,
    html_url: `https://example.com/findings/${finding.id}`,
    _links: {
      self: { href: `/api/v1/scans/scan-id/findings/${finding.id}` },
      results: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results` },
      'representative-results': {
        href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true`,
      },
    },
    _embedded: {
      'representative-results': {
        _links: {
          first: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true` },
          self: { href: `/api/v1/scans/scan-id/findings/${finding.id}/results?representative=true` },
          finding: { href: '#' },
        },
        _embedded: { items: results },
        page: { number: 0, size: 10 },
        total: results.length,
      },
    },
  }
}

// ---------------------------------------------------------------------------
// paginateAndFilter — takes mock findings + URL search params, returns a
// fully typed FindingAggregateResponse with filtered/paginated items and stats.
// ---------------------------------------------------------------------------

export const paginateAndFilter = (
  findings: readonly MockFinding[],
  searchParams: URLSearchParams
): FindingAggregateResponse => {
  const pageSize = Number(searchParams.get('page-size')) || 15
  const pageNumber = Number(searchParams.get('page-number')) || 0

  if (pageSize <= 0) throw new Error('page-size must be greater than 0')
  if (pageNumber < 0) throw new Error('page-number must not be negative')

  const predicate = buildFilterFromParams(searchParams)
  const { items: filteredFindings, stats } = filterAndAccumulate(findings, predicate)

  const startIdx = pageNumber * pageSize
  const paginatedItems = filteredFindings.slice(startIdx, startIdx + pageSize).map(toFindingResponse)

  return {
    _links: {
      first: { href: '/api/v1/scans/scan-id/findings' },
      self: { href: '/api/v1/scans/scan-id/findings' },
    },
    total: stats.total,
    page: { number: pageNumber, size: pageSize },
    _embedded: { items: paginatedItems },
    completed_analysis: stats.completed_analysis,
    in_progress_analysis: stats.in_progress_analysis,
    triage: stats.triage,
    fix: stats.fix,
    sca: stats.sca,
    composite: stats.composite,
  } satisfies FindingAggregateResponse
}
